import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, CheckCircle, MessageCircle, ArrowRight } from 'lucide-react';
import { testimonialsAPI } from '@/lib/api';
import AnimatedSection from './AnimatedSection';

const defaultTestimonials = [
  { name: 'Camila R.', text: 'Vi no Insta e resolvi testar. Peguei o fone bluetooth por R$39 e chegou certinho. Curti demais! 🎧', rating: 5, badge: 'Descobriu pelo Insta', timeAgo: 'hoje', avatar: 'https://ui-avatars.com/api/?name=Camila+R&background=FF6B00&color=fff&size=150' },
  { name: 'Lucas M.', text: 'Minha amiga me mandou o link e achei que era golpe kk mas comprei a air fryer e chegou rapidão 😂', rating: 5, badge: 'Indicação de Amigo', timeAgo: 'ontem', avatar: 'https://ui-avatars.com/api/?name=Lucas+M&background=3B82F6&color=fff&size=150' },
  { name: 'Fernanda S.', text: 'Primeiro site de ofertas que realmente funciona. Já salvei nos favoritos! ⭐', rating: 5, badge: 'Primeira Compra', timeAgo: 'essa semana', avatar: 'https://ui-avatars.com/api/?name=Fernanda+S&background=10B981&color=fff&size=150' },
  { name: 'Rafael P.', text: 'Comprei o ring light por menos da metade do preço. Entrega rápida, recomendo! 📸', rating: 5, badge: 'Primeiro Comprador', timeAgo: 'hoje', avatar: 'https://ui-avatars.com/api/?name=Rafael+P&background=8B5CF6&color=fff&size=150' },
  { name: 'Juliana C.', text: 'Tava procurando um smartwatch barato e achei aqui. Melhor preço que encontrei em qualquer lugar! 💰', rating: 5, badge: 'Descobriu pelo Insta', timeAgo: 'ontem', avatar: 'https://ui-avatars.com/api/?name=Juliana+C&background=EC4899&color=fff&size=150' },
  { name: 'Bruno A.', text: 'Achei por acaso no Google, comprei o carregador turbo e já chegou. Site confiável! ✅', rating: 5, badge: 'Primeira Compra', timeAgo: 'essa semana', avatar: 'https://ui-avatars.com/api/?name=Bruno+A&background=F59E0B&color=fff&size=150' },
];

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideInterval = useRef(null);
  const [slidesPerView, setSlidesPerView] = useState(3);

  useEffect(() => {
    testimonialsAPI.getAll()
      .then((res) => {
        const dbData = res.data || [];
        if (dbData.length >= 2) {
          const mapped = dbData.filter(t => t.is_active !== false).map(t => ({
            name: t.name,
            text: t.text,
            rating: t.rating || 5,
            badge: t.badge || 'Membro do Grupo',
            timeAgo: t.time_ago || 'recente',
            avatar: t.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=FF6B00&color=fff&size=150`,
          }));
          setTestimonials(mapped);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setSlidesPerView(1);
      else if (window.innerWidth < 1024) setSlidesPerView(2);
      else setSlidesPerView(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const maxSlide = Math.max(0, testimonials.length - slidesPerView);

  useEffect(() => {
    if (slideInterval.current) clearInterval(slideInterval.current);
    slideInterval.current = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(slideInterval.current);
  }, [maxSlide]);

  const goToSlide = (idx) => {
    setCurrentSlide(Math.min(idx, maxSlide));
    if (slideInterval.current) clearInterval(slideInterval.current);
    slideInterval.current = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
    }, 4000);
  };

  return (
    <AnimatedSection>
      <section className="py-14 md:py-20 overflow-hidden bg-white" data-testid="testimonials-section">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Quem economiza com a gente recomenda
            </h2>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Junte-se aos primeiros membros do nosso grupo VIP e economize hoje!
            </p>
          </div>

          <div className="relative" data-testid="testimonials-carousel">
            <button
              onClick={() => goToSlide(currentSlide <= 0 ? maxSlide : currentSlide - 1)}
              className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white rounded-full shadow-md border border-slate-200 flex items-center justify-center text-slate-500 hover:text-orange-600 hover:border-orange-200 transition-colors"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => goToSlide(currentSlide >= maxSlide ? 0 : currentSlide + 1)}
              className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white rounded-full shadow-md border border-slate-200 flex items-center justify-center text-slate-500 hover:text-orange-600 hover:border-orange-200 transition-colors"
              data-testid="carousel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="overflow-hidden mx-5 md:mx-7">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * (100 / slidesPerView)}%)` }}
              >
                {testimonials.map((item, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 px-2"
                    style={{ width: `${100 / slidesPerView}%` }}
                    data-testid={`testimonial-slide-${index}`}
                  >
                    <div className="bg-white rounded-xl p-5 h-full flex flex-col" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div className="flex items-start gap-3 mb-3">
                        <img src={item.avatar} alt={item.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" loading="lazy" />
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-slate-900 text-sm leading-tight">{item.name}</p>
                          <div className="flex gap-0.5 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i < item.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200 fill-slate-200'}`} />
                            ))}
                          </div>
                        </div>
                        <span className="text-[11px] text-slate-400 flex-shrink-0 mt-0.5">{item.timeAgo}</span>
                      </div>
                      <div className="mb-3">
                        <span className="inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full font-medium">
                          <CheckCircle className="w-3 h-3" />
                          {item.badge}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed flex-1">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <a
              href="#lead-capture"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('[data-testid="lead-capture-section"]')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full shadow-lg shadow-green-600/20 transition-all duration-300 hover:-translate-y-0.5 text-sm"
              data-testid="testimonials-cta"
            >
              <MessageCircle className="w-4 h-4" />
              Quero fazer parte do grupo VIP
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </AnimatedSection>
  );
};

export default TestimonialsSection;
